import ReactDOM from "react-dom";
import * as PDFJS from "pdfjs-dist";
import { useState, useEffect, useRef } from "react";
import { DocumentIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

import Button from "../../atomic/Button";
import { isImageFunction } from "../../../../../unified-dashboard/src/pages/LoanSanctionConditionTab/functions";

// Helper function to determine if file is an image
const isImageFile = (file: File | string) => {
  if (file instanceof File) return file.type.startsWith("image/");

  if (typeof file === "string")
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file);

  return false;
};

// Helper function to determine if file is a PDF
const isPdfFile = (file: File | string) => {
  if (file instanceof File) return file.type === "application/pdf";

  if (typeof file === "string") return /\.pdf$/i.test(file);

  return false;
};

const UploadedDocumentView = ({
  objectFile,
  objectPath,
  onClose
}: {
  onClose: () => void;
  objectFile?: File | null;
  objectPath?: string | null;
}) => {
  const [objectURL, setObjectURL] = useState<string>("");
  const [fileType, setFileType] = useState<string>("");
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [pageNum, setPageNum] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [isError, setIsError] = useState<boolean>(false);
  const [pdfScale] = useState<number>(1.5);
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // useEffect(() => {
  //   document.addEventListener("contextmenu", (e: any) => e?.preventDefault());
  //   return () => document.removeEventListener("contextmenu", handleContextMenu);
  // }, []);

  useEffect(() => {
    if (objectFile instanceof File && objectFile) {
      const url = URL.createObjectURL(objectFile);
      setObjectURL(url);

      if (isImageFile(objectFile)) {
        setFileType("image");
      } else {
        setFileType("pdf");
        loadPdf(url);
      }

      return () => URL.revokeObjectURL(url);
    } else if (typeof objectPath === "string" && objectPath) {
      setObjectURL(objectPath);

      if (isImageFunction(objectPath)) {
        setFileType("image");
      } else {
        setFileType("pdf");
        loadPdf(objectPath);
      }
    }
  }, [objectFile, objectPath]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      setModalRoot(document.getElementById("modal-root"));
    }
  }, []);

  const loadPdf = (url: string) => {
    // Initialize PDF.js
    PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.js`;

    // Fetch the PDF file and render the first page
    PDFJS?.getDocument(url)
      ?.promise?.then((pdfDoc: any) => {
        setPdfDoc(pdfDoc);
        setPageCount(pdfDoc.numPages);
        renderPage(1, pdfDoc);
        setIsError(false);
      })
      .catch((error: any) => {
        console.error("Error loading PDF:", error);
        setIsError(true);
      });
  };

  const renderPage = (num: number, pdfInfo: any) => {
    if (!pdfInfo || !canvasRef.current) return;

    pdfInfo.getPage(num).then((page: any) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext("2d");
      const viewport = page.getViewport({ scale: pdfScale });

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        enableWebGL: true,
        antialiasText: true
      };

      page.render(renderContext).promise.then(() => {
        // Page rendered
      });

      setPageNum(num);
    });
  };

  const previousPage = () => {
    if (pageNum <= 1) return;
    renderPage(pageNum - 1, pdfDoc);
  };

  const nextPage = () => {
    if (pageNum >= pageCount) return;
    renderPage(pageNum + 1, pdfDoc);
  };

  if (!objectFile && !objectPath) return null;

  return objectURL && modalRoot
    ? ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-70">
          <div className="relative w-full h-full flex justify-center">
            {fileType === "image" && (
              <img
                src={objectURL}
                alt="Preview"
                className="max-h-[90vh] w-auto mx-auto rounded shadow-lg"
              />
            )}

            {fileType === "pdf" && (
              <div className="flex flex-col items-center max-h-[90vh] overflow-y-auto">
                {isError ? (
                  <div className="text-white mt-10">
                    <div className="relative text-center flex justify-center">
                      <DocumentIcon className="h-20 w-20 text-inherit" />
                      <XMarkIcon className="h-10 w-10 text-white absolute top-7" />
                    </div>

                    <div className="font-semibold text-lg mt-5">
                      Something went wrong
                    </div>
                  </div>
                ) : (
                  <>
                    <canvas
                      ref={canvasRef}
                      className="max-w-full rounded shadow-lg"
                    />

                    <div className="flex items-center justify-center mt-6">
                      <Button
                        color="primary"
                        onClick={previousPage}
                        disabled={pageNum === 1}
                        className="!px-3 !py-2"
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-inherit" />
                      </Button>

                      <span className="mx-4 font-semibold text-white">
                        {pageNum} / {pageCount}
                      </span>

                      <Button
                        color="primary"
                        onClick={nextPage}
                        className="!px-3 !py-2"
                        disabled={pageNum === pageCount}
                      >
                        <ChevronRightIcon className="h-5 w-5 text-inherit" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            <button
              onClick={onClose}
              className="absolute right-2 top-0 text-white text-3xl font-bold bg-black bg-opacity-50 rounded-full px-4 py-2 hover:bg-opacity-80" //-translate-y-1/2
            >
              &times;
            </button>
          </div>
        </div>,
        modalRoot
      )
    : null;
};

export default UploadedDocumentView;
